import { useMemo } from 'react'
import { Player } from '../types'
import { MapPin } from 'lucide-react'

// Coordinates in viewBox units (160 x 100). Left = own goal, right = attack.
const POSITION_COORDS: Record<string, { x: number; y: number }> = {
  GK: { x: 12, y: 50 },
  CB: { x: 34, y: 50 },
  LB: { x: 38, y: 14 },
  RB: { x: 38, y: 86 },
  LWB: { x: 64, y: 12 },
  RWB: { x: 64, y: 88 },
  CDM: { x: 58, y: 50 },
  CM: { x: 82, y: 50 },
  CAM: { x: 104, y: 50 },
  LM: { x: 88, y: 16 },
  RM: { x: 88, y: 84 },
  LW: { x: 124, y: 16 },
  RW: { x: 124, y: 84 },
  SS: { x: 122, y: 50 },
  CF: { x: 141, y: 50 },
  ST: { x: 141, y: 50 },
}

// Full names / alternate spellings → short codes (keys are uppercased, no spaces/symbols)
const POSITION_ALIASES: Record<string, string> = {
  GOALKEEPER: 'GK',
  KEEPER: 'GK',
  CENTERBACK: 'CB',
  CENTREBACK: 'CB',
  CENTERHALF: 'CB',
  LEFTBACK: 'LB',
  RIGHTBACK: 'RB',
  LEFTWINGBACK: 'LWB',
  RIGHTWINGBACK: 'RWB',
  DEFENSIVEMIDFIELDER: 'CDM',
  HOLDINGMIDFIELDER: 'CDM',
  DEFENSIVEMID: 'CDM',
  DM: 'CDM',
  CENTRALMIDFIELDER: 'CM',
  CENTERMIDFIELDER: 'CM',
  MIDFIELDER: 'CM',
  CMF: 'CM',
  ATTACKINGMIDFIELDER: 'CAM',
  ATTACKINGMID: 'CAM',
  AM: 'CAM',
  NUMBER10: 'CAM',
  PLAYMAKER: 'CAM',
  LEFTMIDFIELDER: 'LM',
  RIGHTMIDFIELDER: 'RM',
  LEFTWING: 'LW',
  LEFTWINGER: 'LW',
  RIGHTWING: 'RW',
  RIGHTWINGER: 'RW',
  STRIKER: 'ST',
  CENTERFORWARD: 'CF',
  CENTREFORWARD: 'CF',
  CENTRE_FORWARD: 'CF',
  FORWARD: 'CF',
  SECONDSTRIKER: 'SS',
  SUPPORTSTRIKER: 'SS',
}

const normalizePosition = (raw: string): { code: string } | null => {
  const value = (raw || '').trim()
  if (!value || value.toUpperCase() === 'N/A') return null

  const upper = value.toUpperCase()
  const compact = upper.replace(/[^A-Z0-9]/g, '')

  if (POSITION_COORDS[compact]) return { code: compact }
  const alias = POSITION_ALIASES[compact]
  if (alias) return { code: alias }

  if (upper.includes('GOAL')) return { code: 'GK' }
  if (upper.includes('WING') && upper.includes('BACK')) return { code: upper.includes('RIGHT') ? 'RWB' : 'LWB' }
  if (upper.includes('BACK')) return { code: upper.includes('LEFT') ? 'LB' : 'RB' }
  if (upper.includes('DEFENSIVE') || upper.includes('HOLDING')) return { code: 'CDM' }
  if ((upper.includes('ATTACK') || upper.includes('ADVANC')) && upper.includes('MID')) return { code: 'CAM' }
  if (upper.includes('MID') && upper.includes('LEFT')) return { code: 'LM' }
  if (upper.includes('MID') && upper.includes('RIGHT')) return { code: 'RM' }
  if (upper.includes('MID')) return { code: 'CM' }
  if (upper.includes('WINGER') || upper.includes('WING')) return { code: upper.includes('RIGHT') ? 'RW' : 'LW' }
  if (upper.includes('SECOND')) return { code: 'SS' }
  if (upper.includes('STRIK')) return { code: 'ST' }
  if (upper.includes('FORWARD')) return { code: 'CF' }

  return null
}

interface Marker {
  code: string
  x: number
  y: number
  isMain: boolean
}

interface PositionPitchMapProps {
  player: Player
  variant?: 'detail' | 'public'
}

function PositionPitchMap({ player, variant = 'detail' }: PositionPitchMapProps) {
  const markers = useMemo<Marker[]>(() => {
    const mainCode = normalizePosition(player.main_position)?.code
    const secondaryCodes = (player.positions || [])
      .map(p => normalizePosition(p)?.code)
      .filter((c): c is string => Boolean(c))

    const codes: string[] = []
    if (mainCode && !codes.includes(mainCode)) codes.push(mainCode)
    for (const code of secondaryCodes) {
      if (!codes.includes(code)) codes.push(code)
    }
    if (codes.length === 0) return []

    // Place markers, offsetting any that share the same slot (e.g. ST + CF)
    const placed: Marker[] = []
    for (const code of codes) {
      const base = POSITION_COORDS[code] || { x: 80, y: 50 }
      const clash = placed.filter(m => Math.abs(m.x - base.x) < 6 && Math.abs(m.y - base.y) < 8).length
      placed.push({
        code,
        x: base.x,
        y: base.y + clash * 15 - (clash > 0 ? 7 : 0),
        isMain: code === mainCode,
      })
    }
    return placed
  }, [player.main_position, player.positions])

  const cardClass = variant === 'public'
    ? 'bg-[#021a1a] rounded-3xl p-6 border border-white/5'
    : 'bg-[#161b22] border border-gray-800 rounded-3xl p-6'

  const labelClass = variant === 'public'
    ? 'text-xs font-black text-white uppercase tracking-widest'
    : 'text-gray-500 text-[10px] font-black uppercase tracking-widest'

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin size={16} className="text-[#7cff01]" />
          <p className={labelClass}>Position Map</p>
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          {markers.map(m => (
            <span
              key={m.code}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border ${
                m.isMain
                  ? 'bg-[#7cff01]/10 text-[#7cff01] border-[#7cff01]/30'
                  : 'bg-white/5 text-gray-300 border-gray-800'
              }`}
            >
              {m.code}{m.isMain ? ' • Primary' : ''}
            </span>
          ))}
        </div>
      </div>

      <div className="rounded-2xl overflow-hidden border border-white/5 bg-[#071a10]">
        <svg viewBox="0 0 160 100" className="w-full h-auto block" role="img" aria-label="Player positions on pitch">
          {/* Pitch surface */}
          <rect x="2" y="2" width="156" height="96" rx="2" fill="#0a2517" stroke="rgba(255,255,255,0.22)" strokeWidth="0.7" />
          {/* Halfway line */}
          <line x1="80" y1="2" x2="80" y2="98" stroke="rgba(255,255,255,0.22)" strokeWidth="0.7" />
          {/* Centre circle + spot */}
          <circle cx="80" cy="50" r="13" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.7" />
          <circle cx="80" cy="50" r="1.2" fill="rgba(255,255,255,0.35)" />
          {/* Left penalty + six-yard boxes */}
          <rect x="2" y="26" width="24" height="48" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.7" />
          <rect x="2" y="39" width="9" height="22" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.7" />
          <circle cx="17" cy="50" r="1" fill="rgba(255,255,255,0.35)" />
          {/* Right penalty + six-yard boxes */}
          <rect x="134" y="26" width="24" height="48" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.7" />
          <rect x="149" y="39" width="9" height="22" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.7" />
          <circle cx="143" cy="50" r="1" fill="rgba(255,255,255,0.35)" />
          {/* Goals */}
          <rect x="0.5" y="44" width="1.5" height="12" fill="rgba(255,255,255,0.3)" />
          <rect x="158" y="44" width="1.5" height="12" fill="rgba(255,255,255,0.3)" />
          {/* Direction hints */}
          <text x="7" y="95" fill="rgba(255,255,255,0.3)" fontSize="4.2" fontWeight="700" letterSpacing="0.6">OWN GOAL</text>
          <text x="153" y="95" textAnchor="end" fill="rgba(124,255,1,0.5)" fontSize="4.2" fontWeight="700" letterSpacing="0.6">ATTACK</text>

          {markers.length === 0 && (
            <text x="80" y="53" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="6" fontWeight="700">
              NO POSITION ON RECORD
            </text>
          )}

          {markers.map(m => (
            <g key={m.code}>
              {m.isMain && <circle cx={m.x} cy={m.y} r="12" fill="#7cff01" opacity="0.18" />}
              <circle
                cx={m.x}
                cy={m.y}
                r={m.isMain ? 8.5 : 7}
                fill={m.isMain ? '#7cff01' : '#0d1117'}
                stroke="#7cff01"
                strokeWidth={m.isMain ? 0 : 1.1}
              />
              <text
                x={m.x}
                y={m.y + 2.2}
                textAnchor="middle"
                fontSize={m.code.length > 2 ? 5.4 : 6.4}
                fontWeight="900"
                fill={m.isMain ? '#071a0c' : '#7cff01'}
              >
                {m.code}
              </text>
            </g>
          ))}
        </svg>
      </div>

      <p className="mt-3 text-[10px] text-gray-500 uppercase tracking-widest font-bold">
        {markers.length > 0
          ? `Lined up as ${markers.map(m => m.code).join(' • ')} — primary position in green`
          : 'No playable position recorded for this profile'}
      </p>
    </div>
  )
}

export default PositionPitchMap
